import React from 'react';
import { motion } from 'framer-motion';
import AZ from '../../assets/logos/az.png';
import HOT from '../../assets/images/ceo.jpg';

// Palette
const colors = {
  bg: '#FFFFFF',
  gold: '#C69C6D',
  goldDark: '#B88A56',
  goldLight: '#F5E1C8',
  text: '#3B2F2A',
  textSoft: '#5C4A3D',
  glassBg: 'rgba(255, 255, 255, 0.7)',
  glassBorder: 'rgba(198, 156, 109, 0.25)',
  shadow: '0 8px 24px rgba(107, 90, 70, 0.06)',
  shadowHover: '0 16px 32px rgba(184, 138, 86, 0.15)',
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 25 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const imageVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: 'easeOut' } },
  hover: { scale: 1.03, transition: { duration: 0.3 } },
};

const MainContainer2 = () => {
  const paragraphs = [
    "L’équipe Azyekos Services bénéficie d’une grande expérience grâce à un carnet d’adresses exclusif, hype et confidentiel.",
    "Elle opère en République Démocratique du Congo, au Maroc, en Belgique ainsi qu’en Suisse et reste présente sur tous les continents.",
    "Nous organisons vos déplacements, logements, séminaires, banquets et événements.",
    "Vous désirez un moment en centre de bien-être, rencontrer un personal shopper ou encore obtenir une réservation pour un événement particulier, nous prenons soin d’effectuer toutes vos exigences, vous donnant accès à un service clé en main.",
    "Exigez de nous l’excellence et nous serons disponibles à tout moment pour satisfaire toutes vos aspirations, dans un souci permanent de discrétion, d’efficacité et de confiance.",
  ];

  return (
    <>
      <style>{`
        .ceo-section {
          position: relative;
          background: ${colors.bg};
          padding: 100px 0;
          overflow: hidden;
        }

        /* Halos dorés subtils en fond */
        .ceo-section::before,
        .ceo-section::after {
          content: '';
          position: absolute;
          border-radius: 50%;
          filter: blur(120px);
          opacity: 0.2;
          z-index: 0;
          pointer-events: none;
        }

        .ceo-section::before {
          width: 50vw;
          height: 50vw;
          background: ${colors.goldLight};
          top: -15%;
          left: -10%;
          animation: floatOrb 18s ease-in-out infinite;
        }

        .ceo-section::after {
          width: 40vw;
          height: 40vw;
          background: #EAD5C0;
          bottom: -10%;
          right: -5%;
          animation: floatOrb 15s ease-in-out infinite alternate-reverse;
        }

        @keyframes floatOrb {
          0% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(5%, 10%) scale(1.15); }
          100% { transform: translate(-5%, 5%) scale(0.9); }
        }

        .container {
          position: relative;
          z-index: 2;
        }

        /* Logo */
        .logo-glass {
          display: inline-block;
          background: ${colors.glassBg};
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid ${colors.glassBorder};
          border-radius: 24px;
          padding: 15px 30px;
          box-shadow: ${colors.shadow};
          margin-bottom: 50px;
        }

        .logo-img {
          width: 220px;
          height: auto;
          filter: drop-shadow(0 4px 6px rgba(0,0,0,0.04));
        }

        /* Image CEO */
        .ceo-img-wrapper {
          position: relative;
          display: inline-block;
          border-radius: 32px;
          overflow: hidden;
          box-shadow: ${colors.shadow};
          border: 1px solid ${colors.glassBorder};
          background: ${colors.glassBg};
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
        }

        .ceo-img {
          width: 100%;
          height: auto;
          border-radius: 32px;
          display: block;
          object-fit: cover;
          aspect-ratio: 4/5;
        }

        /* Carte texte */
        .text-card {
          background: ${colors.glassBg};
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid ${colors.glassBorder};
          border-radius: 32px;
          padding: 40px;
          box-shadow: ${colors.shadow};
          height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .text-card:hover {
          box-shadow: ${colors.shadowHover};
          border-color: ${colors.gold};
        }

        .main-title {
          font-size: clamp(1.5rem, 4vw, 2rem);
          font-weight: 700;
          color: ${colors.text};
          margin-bottom: 30px;
          line-height: 1.4;
          letter-spacing: -0.3px;
          background: linear-gradient(135deg, ${colors.text}, ${colors.goldDark});
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .paragraph {
          font-size: 1rem;
          color: ${colors.textSoft};
          line-height: 1.8;
          margin-bottom: 16px;
        }

        /* Responsive */
        @media (max-width: 992px) {
          .ceo-section {
            padding: 70px 0;
          }
          .text-card {
            padding: 30px;
          }
          .ceo-img-wrapper {
            margin-bottom: 30px;
          }
        }

        @media (max-width: 576px) {
          .logo-glass {
            padding: 10px 20px;
          }
          .logo-img {
            width: 180px;
          }
          .text-card {
            padding: 25px;
          }
          .main-title {
            font-size: 1.3rem;
          }
          .paragraph {
            font-size: 0.95rem;
          }
        }
      `}</style>

      <section className="ceo-section">
        <div className="container">
          {/* Logo */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="text-center"
          >
            <motion.div variants={itemVariants} className="logo-glass">
              <img src={AZ} alt="Azyekos Logo" className="logo-img" />
            </motion.div>
          </motion.div>

          <motion.div
            className="row align-items-center"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {/* Image CEO */}
            <motion.div
              className="col-lg-5 mb-4 mb-lg-0 d-flex justify-content-center"
              variants={imageVariants}
              whileHover="hover"
            >
              <div className="ceo-img-wrapper">
                <img src={HOT} alt="CEO Azyekos" className="ceo-img" loading="lazy" />
              </div>
            </motion.div>

            {/* Texte */}
            <div className="col-lg-7">
              <motion.div className="text-card" variants={itemVariants}>
                <h1 className="main-title">
                  NOUS VOUS OFFRONS L’EXCEPTION CAR C’EST CE QUE VOUS VALEZ À NOS YEUX
                </h1>
                {paragraphs.map((text, idx) => (
                  <motion.p key={idx} className="paragraph" variants={itemVariants}>
                    {text}
                  </motion.p>
                ))}
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default MainContainer2;